import { ParameterStore } from "@/components/ModelParameters/store/parameter-store";
import { Input } from "@/components/ui/input";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { ParameterEditor } from "./ParameterEditor";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ParameterIcon } from "../../ParameterIcon";
import { Check, Pencil, X } from "lucide-react";

interface ParameterItemProps {
  parameter: ParameterStore;
  index: number;
  onDelete: (index: number) => void;
  onCancel: () => void;
  handleStartEdit: () => void;
  isViewing?: boolean;
}

export const ParameterItem = observer(
  ({
    parameter,
    index,
    onDelete,
    onCancel,
    handleStartEdit,
    isViewing,
  }: ParameterItemProps) => {
    const [previousName, setPreviousName] = useState(parameter.name || "");
    const [isEditing, setIsEditing] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);

    const handleSaveEdit = () => {
      setIsEditing(false);
    };

    const handleOnEdit = () => {
      handleStartEdit();
      setIsEditing(true);
    };

    const handleNameSave = () => {
      setPreviousName(parameter.name || "");
      setIsRenaming(false);
    };

    const handleNameCancel = () => {
      parameter.updateName(previousName);
      setIsRenaming(false);
    };

    const handleCancelEdit = () => {
      onCancel();
      setIsEditing(false);
    };

    return (
      <motion.div
        className="overflow-hidden rounded-md border border-gray-200"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <div className="flex items-center justify-between p-2">
          <div className="group mx-2 flex items-center gap-2">
            <ParameterIcon type={parameter.componentType} />
            <div className="flex items-center gap-2">
              {isRenaming ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={parameter.name}
                    onChange={(e) => parameter.updateName(e.target.value)}
                  />
                  <Button variant="outline" size="sm" onClick={handleNameSave}>
                    <Check className="h-4 w-4 text-primary" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNameCancel}
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="cursor-text whitespace-pre-wrap break-words">
                    {parameter.name}
                  </span>
                  <Pencil
                    onClick={() => setIsRenaming(true)}
                    className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
                  />
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={handleSaveEdit}>
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={handleOnEdit}>
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  className="text-red-500 hover:!bg-red-50 hover:!text-red-600"
                  size="sm"
                  onClick={() => onDelete(index)}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <motion.div
          key="parameter-editor"
          className="border-t border-gray-200 p-3"
          initial={{
            height: 0,
            opacity: 0,
            padding: 0,
          }}
          animate={{
            height: isEditing ? "auto" : 0,
            opacity: isEditing ? 1 : 0,
            padding: isEditing ? "12px" : 0,
          }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="mb-4">
            <Label>Parameter ID</Label>
            <Input
              id="parameter-id"
              value={parameter.paramId}
              onChange={(e) => {
                parameter.updateParamId(e.target.value);
              }}
              readOnly={isViewing}
            />
          </div>
          <ParameterEditor
            isNested
            isViewing={isViewing}
            parameter={parameter}
          />
        </motion.div>
      </motion.div>
    );
  },
);
